import { Button, Heading, HStack, Stack, Text, useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAxios } from 'context/AxiosContextProvider';
import { map, omitBy, property } from 'lodash';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import localconfig from '../../config';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import usePromotionQuery from '../../hooks/usePromotionQuery';
import usePromotionTypesQuery from '../../hooks/usePromotionTypesQuery';
import { ContentCard } from '../../layout/ContentCard';

export interface IPromotionTypeLinkForm {
  promotion_id: number;
  promotion_type_id: number;
}

const PromotionConfigureCard = (props: { promotionId: number }) => {
  const { promotionId } = props;
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const axios = useAxios();

  const promotionQuery = usePromotionQuery(promotionId);
  const promotionTypesQuery = usePromotionTypesQuery();

  // @ts-ignore
  const sendRequest = async ({ url, method = 'put', dataForEndpoint = {} }) => {
    // @ts-ignore
    await axios[method](`${localconfig.apiBaseUrl}/op/v1/promotion/${promotionId}/${url}`, dataForEndpoint)
      .catch((err: { response: { status: number } }) => {
        manageErrorResponse(toast, err.response);
        return Promise.reject(err);
      })
      .then(() => queryClient.invalidateQueries({ queryKey: usePromotionQuery.getKey(promotionId) }));
  };

  const components = promotionQuery.data?.promotion_components || [];
  const availablePromotionTypes = Object.values(
    omitBy(promotionTypesQuery.data?.data ?? [], (promotionType: { id: number }) =>
      map(components, property('promotion_type_id')).includes(promotionType?.id),
    ),
  );

  if (promotionTypesQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <ContentCard header={t('promotions:components.header')}>
      <HStack flexDirection={{ base: 'column', xl: 'row' }} justifyContent="space-between" alignItems="flex-start" rowGap="12">
        <Stack flex={1}>
          <Heading size={'xxs'} mb={5}>
            {t('promotions:components.header')}
          </Heading>
          {availablePromotionTypes.length == 0 ? (
            <Text fontSize="sm" p={2}>
              {t('promotions:no_promotion_types')}
            </Text>
          ) : null}
          {availablePromotionTypes?.map((availablePromotion) => {
            return (
              <HStack key={availablePromotion?.id} justifyContent="space-between" pr={20} pb={2}>
                <Text>{availablePromotion?.name}</Text>
                <Button
                  onClick={() =>
                    sendRequest({
                      url: 'link',
                      dataForEndpoint: {
                        promotion_id: promotionId,
                        promotion_type_id: availablePromotion?.id,
                      },
                    })
                  }
                  color="blue.500"
                >
                  {t('common:add')}
                </Button>
              </HStack>
            );
          })}
        </Stack>
        <Stack flex={1}>
          <Heading size={'xxs'} mb={5}>
            {t('promotions:components.current')}
          </Heading>
          {components.length == 0 ? (
            <Text fontSize="sm" p={2}>
              {t('promotions:no_promotion_components')}
            </Text>
          ) : null}
          {components.map((component) => {
            return (
              <HStack key={component?.id} justifyContent="space-between" pb={2} pr={20}>
                <Text>{component?.promotion_type?.name}</Text>
                <Button
                  onClick={() =>
                    sendRequest({
                      url: 'unlink',
                      dataForEndpoint: {
                        promotion_type_id: component?.promotion_type_id,
                      },
                    })
                  }
                  color="red.500"
                >
                  {t('common:delete')}
                </Button>
              </HStack>
            );
          })}
        </Stack>
      </HStack>
    </ContentCard>
  );
};

export default PromotionConfigureCard;
